import { Stack, Typography } from '@mui/material';
import classNames from 'classnames';
import { isFunction, uniq, uniqBy } from 'lodash-es';
import bind from 'memoize-bind';
import moment from 'moment-timezone';
import React, { cloneElement, Component, Fragment, ReactNode } from 'react';
import Highlighter from 'react-highlight-words';

import DownArrowIcon from '@sbu/assets.svg/src/single-chevron-down.svg';
import { Checkbox } from '@sbu/components.checkbox';
import { Loader } from '@sbu/components.loader';
import { Motion } from '@sbu/components.motion';
import { Button, Divider } from '@sumologic/yama-ds-components';

import { Calendar, CalendarChangeEvent } from 'shared/components/form/Calendar';
import { Time } from 'shared/components/form/Time';
import { ClickLink } from 'shared/components/internal/ClickLink';
import { Collapse } from 'shared/components/layout/Collapse';
import { List, ListHeader, ListItem } from 'shared/components/layout/List';
import { Slant } from 'shared/components/utils/Slant';
import { getNormalizedCypressKey } from 'shared/utils';

import { DateFormat } from '../../Moment';
import {
  AllOperators,
  DateField,
  Field,
  FieldTypes,
  FilterTypes,
  isNegatableOptionsList,
  KeywordFilter,
  ListField,
  ListFieldOption,
  Modifier,
  NegatableListFieldOption,
  NumericField,
  Operator,
  PropBasedFilterTypes,
  Source,
  SuggestedFilter,
  ValueBasedFilterTypes,
} from '../types';
import {
  flattenOptionGroups,
  getFieldObject,
  getFilterValueLabel,
  getOperatorObject,
  isDateRangeFilter,
  isEqualityFilter,
  isKeyValueFilter,
  isRelativeRangeFilter,
  ProcessedField,
} from '../utils';
import css from './FilterDropdown.module.scss';

export interface OnSelectValueProps {
  value: any;
  endValue?: Date;
  index?: number;
}

interface FilterDropdownProps {
  activeSource: string;
  inputText?: string;
  suggestedFilters: SuggestedFilter[];
  options: ListFieldOption[] | NegatableListFieldOption[];
  fields: FieldTypes[];
  allFields: FieldTypes[];
  operators: Operator[];
  units: Modifier[];
  sources: Source[];
  allSources: Source[];
  showSources: boolean;
  style?: any;
  activeOptionIndex: number;
  partialFilter: Partial<FilterTypes>;
  showTime?: boolean;
  includeSeconds?: boolean;
  dateSelection?: Date;
  dateRangeEndSelection?: Date;
  variant: 'default' | 'compact';
  view?: 'source' | 'fields' | 'operators' | 'value' | 'modifier';
  expandedFields: { [key: string]: boolean };
  processedFields: ProcessedField[];
  flatProcessedFields: ProcessedField[];
  autocompleteList: string[];
  includeCurrent: boolean;
  loading?: boolean;
  onSelectSource: (source: string) => void;
  onToggleFieldVisibility: (prop: string) => void;
  onToggleTime: (show: boolean) => void;
  onToggleIncludeCurrent: () => void;
  onSuggestionClick: (suggestion: SuggestedFilter) => void;
  onSelectField: (field: string) => void;
  onSelectOperator: (operator: AllOperators) => void;
  onSelectModifier: (unit: Modifier) => void;
  onSelectValue: (props: OnSelectValueProps) => void;
  onDateSelection: (value: Date, endValue?: Date) => void;
}

export class FilterDropdown extends Component<FilterDropdownProps> {
  getLabel(item: any) {
    return item.formattedLabel || item.label;
  }

  onMouseDown(event: React.MouseEvent) {
    event.preventDefault();
  }

  onDateSelect(event: CalendarChangeEvent) {
    const { showTime, onDateSelection } = this.props;
    const value = event.startDate ? event.startDate : new Date();
    const endValue = event.endDate ? event.endDate : undefined;

    let momentVal = moment(value);
    let momentEndVal = moment(endValue);
    if (!showTime) {
      momentVal = momentVal.startOf('day');
      momentEndVal = momentEndVal.startOf('day');
    }

    const dateSelection = momentVal.toDate();
    const dateRangeEndSelection = endValue ? momentEndVal.toDate() : undefined;

    onDateSelection(dateSelection, dateRangeEndSelection);
  }

  onTimeChange(date: Date, dateRangeEnd: Date) {
    const { dateRangeEndSelection } = this.props;
    this.props.onDateSelection(date, dateRangeEnd || dateRangeEndSelection);
  }

  onToggleTime() {
    this.props.onToggleTime(!this.props.showTime);
  }

  onDateApply() {
    const { onSelectValue, dateSelection, dateRangeEndSelection } = this.props;

    if (dateSelection && dateRangeEndSelection) {
      onSelectValue({ value: dateSelection, endValue: dateRangeEndSelection });
    } else if (dateSelection) {
      onSelectValue({ value: dateSelection });
    }
  }

  getSuggestions() {
    const { view, inputText, suggestedFilters } = this.props;

    if (!suggestedFilters || !suggestedFilters.length || inputText) {
      return [];
    }

    if (view === 'fields') {
      return suggestedFilters.filter((s) => !s.view || s.view === 'field');
    } else if (view === 'operators') {
      const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;

      const operatorMatches = suggestedFilters.filter((suggestion) => {
        if (!(suggestion.view === 'operator' || !suggestion.view)) {
          return false;
        }

        const filters = !Array.isArray(suggestion.filter)
          ? ([suggestion.filter] as PropBasedFilterTypes[])
          : (suggestion.filter as PropBasedFilterTypes[]);

        return filters.find((f) => f.prop === partialFilter.prop) !== undefined;
      });

      return operatorMatches;
    }

    return [];
  }

  onToggleField(prop: string) {
    this.props.onToggleFieldVisibility(prop);
  }

  getSuggestionLabels(suggestion: FilterTypes | FilterTypes[]) {
    const { allFields } = this.props;

    if (Array.isArray(suggestion)) {
      return suggestion.map((s) => this.getSuggestionLabels(s));
    } else {
      const suggestedFilter = suggestion as ValueBasedFilterTypes;

      let fieldObject;
      if (suggestedFilter.prop) {
        fieldObject = allFields.find(
          (field) => field.prop === suggestedFilter.prop
        );
      }

      const fieldLabel = fieldObject ? fieldObject.label : '';
      const operator = getOperatorObject(
        suggestedFilter.operator,
        suggestedFilter,
        allFields
      );
      const operatorLabel = operator ? operator.label : '';
      const valueLabel = getFilterValueLabel(fieldObject, suggestedFilter);

      return [`${fieldLabel} ${operatorLabel} ${valueLabel}`];
    }
  }

  renderSuggestion(suggestion: SuggestedFilter, index: number) {
    const { onSuggestionClick } = this.props;
    const labels = this.getSuggestionLabels(suggestion.filter);

    return (
      <ListItem
        className={css.listOption}
        data-sumo-test={`suggestion-${getNormalizedCypressKey(labels)}`}
        key={`suggestion-${index}`}
        onClick={() => onSuggestionClick(suggestion)}
      >
        {labels.map((label, index) => (
          <Fragment key={label}>
            <Typography>{label}</Typography>
            {index !== labels.length - 1 && (
              <Typography variant='body2' color='text.secondary'>
                AND
              </Typography>
            )}
          </Fragment>
        ))}
      </ListItem>
    );
  }

  renderSuggestions(suggestions: SuggestedFilter[]) {
    if (!suggestions.length) {
      return null;
    }

    const suggestionsGroup = [
      {
        label: 'Quick Filters',
        suggestions: suggestions.filter((s) => s.type === 'filter'),
      },
      {
        label: 'Suggestions',
        suggestions: suggestions.filter((s) => s.type === 'suggestion'),
      },
      {
        label: 'Previous Searches',
        suggestions: suggestions.filter((s) => s.type === 'history'),
      },
    ];

    return (
      <Fragment>
        {suggestionsGroup.map((suggestion) => (
          <Fragment key={suggestion.label}>
            {suggestion.suggestions.length > 0 && (
              <div className={css.suggestionsContainer}>
                <Divider
                  orientation='vertical'
                  sx={{ left: 0, position: 'absolute' }}
                />
                <List>
                  <ListHeader className={css.listHeader}>
                    {suggestion.label}
                  </ListHeader>
                  {suggestion.suggestions.map(this.renderSuggestion.bind(this))}
                </List>
              </div>
            )}
          </Fragment>
        ))}
      </Fragment>
    );
  }

  renderOption(index: number, optionLabel: string) {
    const { activeOptionIndex } = this.props;

    return (
      <ListItem
        className={classNames(css.listOption, {
          [css.active]: activeOptionIndex === index,
        })}
        data-sumo-test={`option-${getNormalizedCypressKey(optionLabel)}`}
        key={optionLabel}
      >
        <Typography>{optionLabel}</Typography>
      </ListItem>
    );
  }

  renderFieldList(fields: ProcessedField[]) {
    const {
      onSelectField,
      activeOptionIndex,
      expandedFields,
      inputText,
      flatProcessedFields,
    } = this.props;

    return fields.reduce((acc: ReactNode[], field: ProcessedField) => {
      // Get the index from the source of truth of how its rendered
      const index = flatProcessedFields.findIndex((f) => f.prop === field.prop);

      if (field.children) {
        // If we have inputted text, we should expand everything
        const isExpanded = inputText !== '' || expandedFields[field.prop];

        acc.push(
          <Fragment key={`field-${field.prop}-${index}`}>
            <ListItem
              className={classNames(css.listOption, css.toggleListOption, {
                [css.active]: activeOptionIndex === index,
                [css.listOptionHasParent]: field.parent,
              })}
              data-sumo-test={`field-${getNormalizedCypressKey(
                field.label || field.prop
              )}`}
              onClick={() => this.onToggleField(field.prop)}
            >
              <div className={css.toggleListOptionContainer}>
                <Typography flex={1}>{field.label}</Typography>
                <DownArrowIcon
                  className={classNames(css.toggleListIcon, {
                    [css.toggleIconExpanded]: isExpanded,
                  })}
                />
              </div>
            </ListItem>
            <Collapse expanded={isExpanded}>
              <List className={css.nestedList}>
                {this.renderFieldList(field.children!)}
              </List>
            </Collapse>
          </Fragment>
        );
      } else {
        acc.push(
          <ListItem
            className={classNames(css.listOption, {
              [css.active]: activeOptionIndex === index,
              [css.listOptionHasParent]: field.parent,
            })}
            data-sumo-test={`field-${getNormalizedCypressKey(
              field.label || field.prop
            )}`}
            key={`field-${field.prop}-${index}`}
            middleAlign={true}
            onClick={() => onSelectField(field.prop)}
          >
            <Typography>{field.label}</Typography>
          </ListItem>
        );
      }

      return acc;
    }, []);
  }

  renderFields() {
    const { fields, processedFields, loading } = this.props;

    return (
      <List>
        <ListHeader className={css.listHeader}>Fields</ListHeader>
        {this.renderFieldList(processedFields)}
        {loading && <Loader mt={1} mb={2} />}
        {!loading && fields.length === 0 && (
          <ListItem className={css.listOption}>No matching fields</ListItem>
        )}
      </List>
    );
  }

  renderOperators() {
    const { operators, onSelectOperator } = this.props;

    return (
      <List>
        <ListHeader className={css.listHeader}>Operators</ListHeader>
        {uniq(operators).map((operator, index) =>
          cloneElement(this.renderOption(index, this.getLabel(operator)), {
            onClick: () => onSelectOperator(operator.type),
          })
        )}
        {operators.length === 0 && (
          <ListItem className={css.listOption}>No matching operators</ListItem>
        )}
      </List>
    );
  }

  renderModifiers() {
    const {
      units,
      onSelectModifier,
      allFields,
      includeCurrent,
      onToggleIncludeCurrent,
    } = this.props;
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;
    const field = getFieldObject(partialFilter.prop, allFields) as DateField;
    const { modifiers } = field;
    const filteredUnits = modifiers
      ? units.filter((u) => modifiers.find((m) => u.type === m.type))
      : units;

    return (
      <List>
        <ListItem className={css.includeCurrentModifier}>
          <Checkbox
            CheckboxProps={{
              checked: includeCurrent,
              onChange: onToggleIncludeCurrent,
              sx: { padding: 0 },
            }}
            label='Include current'
          />
        </ListItem>
        {filteredUnits.map((unit, index) =>
          cloneElement(this.renderOption(index, this.getLabel(unit)), {
            onClick: () => onSelectModifier(unit),
          })
        )}
        {filteredUnits.length === 0 && (
          <ListItem className={css.listOption}>No matching options</ListItem>
        )}
      </List>
    );
  }

  renderListOptionGroup(
    group: string,
    options: ListFieldOption[] | NegatableListFieldOption[],
    allGroups: string[]
  ) {
    const groupedOptions = options.filter((o) => o.group === group);

    if (groupedOptions.length) {
      if (!group) {
        return this.renderListOptions(groupedOptions, allGroups);
      } else {
        return (
          <Fragment key={group}>
            <ListHeader className={css.listHeader}>{group}</ListHeader>
            {this.renderListOptions(groupedOptions, allGroups)}
          </Fragment>
        );
      }
    }

    return null;
  }

  renderListOption(
    option: ListFieldOption | NegatableListFieldOption,
    index: number
  ) {
    const { onSelectValue, activeOptionIndex } = this.props;
    const label = this.getLabel(option);

    return (
      <ListItem
        className={classNames(css.listOption, {
          [css.active]: activeOptionIndex === index,
        })}
        data-sumo-test={`list-option-${getNormalizedCypressKey(option.label)}`}
        key={`${option.label}-${index}`}
        onClick={() => onSelectValue({ value: option.prop, index })}
      >
        {option.listTemplate !== undefined &&
          (isFunction(option.listTemplate)
            ? option.listTemplate(label)
            : option.listTemplate)}
        {option.listTemplate === undefined && <Typography>{label}</Typography>}
      </ListItem>
    );
  }

  renderListOptions(
    groupOptions: ListFieldOption[] | NegatableListFieldOption[],
    allGroups?: string[]
  ) {
    const { options } = this.props;

    if (
      isNegatableOptionsList(groupOptions) &&
      isNegatableOptionsList(options)
    ) {
      const resortedGroups = flattenOptionGroups(
        options,
        allGroups
      ) as NegatableListFieldOption[];

      return uniq(groupOptions).map((option) =>
        this.renderListOption(option, resortedGroups.indexOf(option))
      );
    } else {
      const resortedGroups = flattenOptionGroups(
        options,
        allGroups
      ) as ListFieldOption[];

      return uniq(groupOptions).map((option) =>
        this.renderListOption(option, resortedGroups.indexOf(option))
      );
    }
  }

  renderGenericValue(field: FieldTypes) {
    const { inputText } = this.props;

    return (
      <div className={css.textHint}>
        {inputText !== '' && (
          <Fragment>
            Press <span className={css.keyName}>enter</span> to filter by &nbsp;
            <i>&quot;{inputText}&quot;...</i>
          </Fragment>
        )}
        {inputText === '' && (
          <Fragment>
            Type a value to filter <i>{field.label}</i> on...
          </Fragment>
        )}
      </div>
    );
  }

  renderListValues() {
    const { allFields } = this.props;
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;
    const field = getFieldObject(partialFilter.prop, allFields) as ListField;

    if (!field.options) {
      return this.renderGenericValue(field);
    } else {
      const { options } = this.props;
      const groups = uniqBy(options, (o) => o.group || '').map(
        (o) => o.group
      ) as string[];
      const isGrouped = !!groups.filter((g) => g !== undefined).length;

      return (
        <List>
          {isGrouped && (
            <Fragment>
              {groups.map((group) =>
                this.renderListOptionGroup(group, options, groups)
              )}
            </Fragment>
          )}
          {!isGrouped && this.renderListOptions(options)}
          {(!options || options.length === 0) && (
            <ListItem className={css.listOption}>No matching options</ListItem>
          )}
        </List>
      );
    }
  }

  canApplyDate(isRangeFilter: boolean) {
    const { dateSelection, dateRangeEndSelection } = this.props;
    let canApply = false;

    if (isRangeFilter) {
      canApply = !dateSelection || !dateRangeEndSelection;
    } else {
      canApply = !dateSelection;
    }

    return canApply;
  }

  renderCalendarValues() {
    const {
      allFields,
      dateRangeEndSelection,
      dateSelection,
      includeSeconds,
      showTime,
    } = this.props;
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;
    const allowRange = isDateRangeFilter(partialFilter);
    const equalityFilter = isEqualityFilter(partialFilter);
    const field = getFieldObject(partialFilter.prop, allFields) as DateField;
    const maxDate = field.disableFuture ? new Date() : undefined;
    const minDate = field.disablePast ? new Date() : undefined;

    return (
      <Stack alignItems='center'>
        <Calendar
          active={dateSelection}
          activeEnd={dateRangeEndSelection}
          className={css.calendarContainer}
          isRange={allowRange}
          maxDate={maxDate}
          minDate={minDate}
          onChange={bind(this.onDateSelect, this)}
          showToday={!allowRange}
        />
        {showTime && (!equalityFilter || includeSeconds) && (
          <div className={css.timeContainer}>
            <div className={css.startTime}>
              <div className={css.dateLabel}>
                {dateSelection && (
                  <DateFormat
                    date={moment(dateSelection)}
                    formattingOptions={{ year: true }}
                  />
                )}
              </div>
              <div>
                <Time
                  active={dateSelection}
                  includeSeconds={includeSeconds}
                  isMilitary={false}
                  onChange={(date) =>
                    // @ts-ignore
                    this.onTimeChange(date, dateRangeEndSelection)
                  }
                />
              </div>
            </div>
            {allowRange && (
              <Fragment>
                <div className={css.timeDivider}>TO</div>
                <div className={css.endTime}>
                  <div className={css.dateLabel}>
                    {dateRangeEndSelection && (
                      <DateFormat
                        date={moment(dateRangeEndSelection)}
                        formattingOptions={{ year: true }}
                      />
                    )}
                  </div>
                  <div>
                    <Time
                      active={dateRangeEndSelection}
                      disabled={!dateRangeEndSelection}
                      includeSeconds={includeSeconds}
                      isMilitary={false}
                      // @ts-ignore
                      onChange={(end) => this.onTimeChange(dateSelection, end)}
                    />
                  </div>
                </div>
              </Fragment>
            )}
          </div>
        )}
        {!equalityFilter && (
          <ClickLink
            className={css.toggleTimeLink}
            onClick={bind(this.onToggleTime, this)}
          >
            {showTime ? 'Remove Time' : 'Add Time'}
          </ClickLink>
        )}
        <Button
          disabled={this.canApplyDate(allowRange)}
          onClick={bind(this.onDateApply, this)}
          sx={{ m: 1.875 }}
        >
          Apply
        </Button>
      </Stack>
    );
  }

  renderTextValues(field: Field) {
    const { autocompleteList } = this.props;
    const hasOptions = autocompleteList.length > 0;

    return (
      <Fragment>
        {this.renderGenericValue(field)}
        {hasOptions && (
          <Fragment>
            <Divider />
            {this.renderAutocompleteList()}
          </Fragment>
        )}
      </Fragment>
    );
  }

  renderNumericValues(field: NumericField) {
    const { inputText, autocompleteList } = this.props;
    const hasOptions = autocompleteList.length > 0;
    const hasMin = field.min !== undefined;
    const hasMax = field.max !== undefined;

    return (
      <Fragment>
        <div className={css.textHint}>
          {inputText !== '' && (
            <Fragment>
              Press <span className={css.keyName}>enter</span> to filter by
              &nbsp;
              <i>&quot;{inputText}&quot;...</i>
            </Fragment>
          )}
          {inputText === '' && (
            <Fragment>
              Type a numeric value
              {hasMin && hasMax && ` between ${field.min} and ${field.max} `}
              {hasMin && !hasMax && ` greater than or equal to ${field.min} `}
              {hasMax && !hasMin && ` less than or equal to ${field.max} `}
              to filter <i>{field.label}</i> on...
            </Fragment>
          )}
        </div>
        {hasOptions && (
          <Fragment>
            <Divider />
            {this.renderAutocompleteList()}
          </Fragment>
        )}
      </Fragment>
    );
  }

  renderAutocompleteList() {
    const { autocompleteList, onSelectValue, inputText, activeOptionIndex } =
      this.props;

    return (
      <List>
        <ListHeader className={css.listHeader}>Suggestions</ListHeader>
        {autocompleteList.map((value, index) => (
          <ListItem
            className={classNames(css.listOption, {
              [css.active]: activeOptionIndex === index,
            })}
            key={value}
            onClick={() => onSelectValue({ value })}
          >
            <Highlighter
              autoEscape={true}
              searchWords={[inputText]}
              textToHighlight={value}
            />
          </ListItem>
        ))}
      </List>
    );
  }

  renderIpValues(field: Field) {
    const { inputText, autocompleteList } = this.props;
    const hasOptions = autocompleteList.length > 0;

    return (
      <Fragment>
        <div className={css.textHint}>
          {inputText !== '' && (
            <Fragment>
              Press <span className={css.keyName}>enter</span> to filter by
              &nbsp;
              <i>&quot;{inputText}&quot;...</i>
            </Fragment>
          )}
          {inputText === '' && (
            <Fragment>
              Type an IP4 or IP6 address to filter <i>{field.label}</i> on...
            </Fragment>
          )}
        </div>
        {hasOptions && (
          <Fragment>
            <Divider />
            {this.renderAutocompleteList()}
          </Fragment>
        )}
      </Fragment>
    );
  }

  renderRegexHint() {
    return (
      <div className={css.textHint}>
        Type a regular expression to match against the entire field...
      </div>
    );
  }

  renderWildcardHint() {
    return (
      <div className={css.textHint}>
        Type a wildcard pattern using * to represent zero or more characters...
      </div>
    );
  }

  renderIpRangeHint() {
    return (
      <div className={css.textHint}>
        Type an IP4 range (e.g. 192.0.2.0/24) to filter on...
      </div>
    );
  }

  // complexity 16
  // eslint-disable-next-line complexity
  renderValues() {
    const { fields } = this.props;
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;
    const keywordFilter = this.props.partialFilter as KeywordFilter;
    const field = getFieldObject(partialFilter.prop, fields);
    const isRelativeRange = isRelativeRangeFilter(partialFilter);

    if (field) {
      if (field.type === 'date' && !isRelativeRange) {
        return this.renderCalendarValues();
      } else if (field.type === 'date' && isRelativeRange) {
        return this.renderModifierValue();
      } else if (field.type === 'list') {
        return this.renderListValues();
      } else if (field.type === 'numeric') {
        return this.renderNumericValues(field as NumericField);
      } else if (field.type === 'text') {
        if (
          ['matchesRegex', 'doesNotMatchRegex'].includes(partialFilter.operator)
        ) {
          return this.renderRegexHint();
        } else if (
          ['matchesWildcard', 'doesNotMatchWildcard'].includes(
            partialFilter.operator
          )
        ) {
          return this.renderWildcardHint();
        } else {
          return this.renderTextValues(field);
        }
      } else if (field.type === 'ip') {
        if (
          ['isInIpRange', 'isNotInIpRange'].includes(partialFilter.operator)
        ) {
          return this.renderIpRangeHint();
        } else {
          return this.renderIpValues(field);
        }
      } else if (field.type === 'keyValue') {
        if (!isKeyValueFilter(partialFilter)) {
          return this.renderTextValues(field);
        }
      }
    } else if (keywordFilter.operator === 'keyword') {
      return this.renderKeywordHint();
    }

    return null;
  }

  renderKeywordHint() {
    const { inputText, activeSource, showSources, allSources, view } =
      this.props;

    // Record searches can't have keywords
    const source = allSources.find((s) => s.prop === activeSource);
    const canKeyword = !source || source.allowKeywords !== false;
    const showKeywordHint = inputText && view === 'fields' && canKeyword;
    const sourceLabel = source ? source.label : '';

    if (!showKeywordHint) {
      return null;
    }

    return (
      <div className={css.keywordHint}>
        <span className={css.keywordHintText}>
          Press <span className={css.keyName}>enter</span> to keyword search
          {inputText && (
            <Fragment>
              &nbsp;for&nbsp;
              <i>&quot;{inputText}&quot;</i>
            </Fragment>
          )}
          {showSources && sourceLabel && <Fragment> in {sourceLabel}</Fragment>}
        </span>
        <Divider />
      </div>
    );
  }

  renderModifierValue() {
    return (
      <div className={css.textHint}>
        Enter a number to filter duration on (e.g. 24) then
        <br />
        press <span className={css.keyName}>enter</span>
        to select a period (days/months/etc).
      </div>
    );
  }

  renderSources() {
    const { sources, onSelectSource, activeOptionIndex, activeSource } =
      this.props;

    return (
      <List>
        <ListHeader className={css.listHeader}>Sources</ListHeader>
        {sources.map((source, index) => (
          <ListItem
            className={classNames(css.listOption, {
              [css.active]:
                activeOptionIndex === index || activeSource === source.prop,
            })}
            data-sumo-test={`source-${source.prop}`}
            key={`source-${source.prop}`}
            middleAlign={true}
            onClick={() => onSelectSource(source.prop)}
          >
            {source.icon !== undefined &&
              cloneElement(source.icon, { className: css.fieldIcon })}
            <Typography>{this.getLabel(source)}</Typography>
          </ListItem>
        ))}
      </List>
    );
  }

  render() {
    const { showSources, style, variant, view } = this.props;
    const suggestions = this.getSuggestions();

    const className = classNames(css.dropdown, {
      [css.contentHasSuggestions]: suggestions.length,
      [css.compactStyle]: variant === 'compact',
      [css.defaultStyle]: variant === 'default',
      [css.showSources]: showSources,
    });

    return (
      <Slant className={className} style={style}>
        <Motion type='group'>
          {this.renderKeywordHint()}
          <div className={css.contentLists} data-sumo-test='filter-dropdown'>
            <div
              className={classNames(css.listContainer, {
                [css.fieldsContainer]: view === 'fields',
                [css.operatorsContainer]: view === 'operators',
              })}
            >
              {view === 'source' && this.renderSources()}
              {view === 'fields' && this.renderFields()}
              {view === 'operators' && this.renderOperators()}
              {view === 'value' && this.renderValues()}
              {view === 'modifier' && this.renderModifiers()}
            </div>
            {this.renderSuggestions(suggestions)}
          </div>
        </Motion>
      </Slant>
    );
  }
}
